import { FC, useEffect, useState } from 'react';
import {
  Table,
  TableHeading,
  TableCell,
  Text,
  Button,
  Snackbar,
  Snack,
  StatusIconProtoProps,
  Skeleton,
} from '@nike/eds';
import { useI18nContext } from '../../context/language/use-i18n-context';
import { useSponsorContext } from '../../context/Sponsor';
import ts from './i18n';
import Banner from 'components/shared/banner/banner';
import styles from './myaccess.module.css';
import { VisitorPass } from '../../models/IVisitorPass';
import { GetEmployeePassV2 } from '../../services/pass-service';
import NikeStoreTealIcon from '../../assets/img/Nike-Store-Icon-Teal.png';
import NikeStoreGreyIcon from '../../assets/img/Nike-Store-Icon-Grey.png';
import SwooshTealIcon from '../../assets/img/Swoosh-Icon-Teal.png';
import SwooshGreyIcon from '../../assets/img/Swoosh-Icon-Grey.png';
import SwooshYellowIcon from '../../assets/img/Swoosh-Icon-Yellow.png';
import ConverseTealIcon from '../../assets/img/Converse-Star-Teal.png';
import ConverseGreyIcon from '../../assets/img/Converse-Star-Grey.png';
import MyAccessForm from './myaccess-form';
import { GetManager } from 'services/user-service';
import { GetRegistrationLink } from 'services/pass-service';

export const MyAccess: FC = () => {
  const { i18nString } = useI18nContext();
  const sponsorContext = useSponsorContext();
  const sponsorDetails = sponsorContext?.sponsor;
  const sponsorCountry = sponsorDetails?.country;
  const isChinaRegion = sponsorCountry === 'China';

  const [accessAccountList, setAccessAccountList] = useState<VisitorPass[]>();
  const [error, setError] = useState<string>();
  const [manageAccessModal, setManageAccessModal] = useState<boolean>(false);
  const [managerId, setManager] = useState<string>('');
  const [swooshRegistrationLink, setSwooshRegistrationLink] = useState<string>('');
  const [snackBar, setSnackbar] = useState({
    showSnack: false,
    message: '',
    snackStatus: 'success' as StatusIconProtoProps['status'],
  });

  useEffect(() => {
    if (sponsorDetails?.sponsorId && manageAccessModal === false) {
      GetEmployeePassV2(sponsorDetails?.sponsorId)
        .then(response => {
          setAccessAccountList(response.data);

          const swooshPass = response.data.find(
            x => x.passTypeToken === 'PASS_TYPE_SWOOSH_EMPLOYEE',
          );
          if (swooshPass) {
            GetRegistrationLink(sponsorDetails?.sponsorId, swooshPass.passId)
              .then(response => {
                setSwooshRegistrationLink(response.data.registrationLink);
              })
              .catch(error => {
                setError(error);
              });
          }
        })
        .catch(error => {
          setError(error);
        });

      const manager = GetManager();
      setManager(manager?.id || '');
    }
  }, [sponsorDetails?.sponsorId, manageAccessModal]);

  const columns = [
    {
      headerName: ts.GRID_HEADER_NAME.stringKey,
      field: 'name',
    },
    {
      headerName: ts.GRID_HEADER_EMAIL.stringKey,
      field: 'email',
    },
    {
      headerName: ts.ACCESS_TEXT.stringKey,
      field: 'access',
    },
    {
      headerName: ts.GRID_HEADER_STATUS.stringKey,
      field: 'status',
    },
  ];

  const getAccessDetails = (passDetails: VisitorPass) => {
    switch (passDetails.passTypeToken) {
      case 'PASS_TYPE_EMPLOYEE':
        return getEmployeePass(passDetails);

      case 'PASS_TYPE_CONVERSE_EMPLOYEE':
        return getConversePass(passDetails);

      case 'PASS_TYPE_SWOOSH_EMPLOYEE':
        return getSwooshPass(passDetails);
    }
  };

  const getEmployeePass = (employeePass: VisitorPass) => {
    const passStatus = employeePass.statusToken;
    let iconSource = NikeStoreGreyIcon;
    if (passStatus === 'PASS_STATUS_REGISTERED') iconSource = NikeStoreTealIcon;

    return (
      <tr key={employeePass.passId}>
        <TableCell className={styles.table_td}>{sponsorDetails?.displayName}</TableCell>
        <TableCell className={styles.table_td}>{employeePass.emailAddress}</TableCell>
        <TableCell className={styles.table_td}>
          <img title="Store" className={styles.grid_image} src={iconSource}></img>
        </TableCell>
        <TableCell className={styles.table_td}>
          {i18nString(ts[employeePass.statusToken as keyof typeof ts])}
        </TableCell>
      </tr>
    );
  };

  const getConversePass = (conversePass: VisitorPass) => {
    const passStatus = conversePass.statusToken;
    let iconSource = ConverseGreyIcon;
    if (passStatus === 'PASS_STATUS_REGISTERED') iconSource = ConverseTealIcon;

    return (
      <>
        <tr key={conversePass.passId}>
          <TableCell className={styles.table_td}></TableCell>
          <TableCell className={styles.table_td}>{conversePass.emailAddress}</TableCell>
          <TableCell className={styles.table_td}>
            <img title="Converse" className={styles.grid_image} src={iconSource}></img>
          </TableCell>
          <TableCell className={styles.table_td}>
            {i18nString(ts[conversePass.statusToken as keyof typeof ts])}
          </TableCell>
        </tr>
      </>
    );
  };

  const getSwooshPass = (swooshPass: VisitorPass) => {
    let passStatus = swooshPass.statusToken;
    const isPendingApproval = swooshPass.passStatusId === 0;

    let iconSource = SwooshGreyIcon;
    if (passStatus === 'PASS_STATUS_REGISTERED') iconSource = SwooshTealIcon;
    if (isPendingApproval) iconSource = SwooshYellowIcon;

    if (isChinaRegion && swooshPass.passStatusId === 1 && !swooshPass.passPlayIdRegistered)
      passStatus = 'PASS_STATUS_APPROVED';

    return (
      <>
        <tr key={swooshPass.passId}>
          <TableCell className={styles.table_td}></TableCell>
          <TableCell className={styles.table_td}>{swooshPass.emailAddress}</TableCell>
          <TableCell className={styles.table_td}>
            <img title="Swoosh" className={styles.grid_image} src={iconSource}></img>
          </TableCell>
          <TableCell className={styles.table_td}>
            {i18nString(ts[passStatus as keyof typeof ts])}
          </TableCell>
        </tr>
      </>
    );
  };

  return (
    <>
      <section
        className={`${styles.myaccess_BannerSection} eds-flex eds-flex--flex-direction-row section-cont`}
      >
        <div className="eds-flex eds-flex--justify-content-center div-flex pass_avl">
          <Text as={'h3'} color="white" className="h3-20">
            {i18nString(ts.MYACCESS_MANAGESTORE_TEXT)}
          </Text>
          {(accessAccountList ?? []).length > 0 ? (
            <Button
              size="small"
              variant="primary"
              data-testid="manage-myaccess"
              className="btn_green eds-spacing--my-20 txt-black"
              onClick={() => setManageAccessModal(true)}
            >
              {i18nString(ts.MANAGEMYACCESS_TEXT)}
            </Button>
          ) : (
            <Skeleton
              height={30}
              width={'30%'}
              className="btn_green eds-spacing--my-20 txt-black"
            />
          )}
        </div>
        <Banner width="65%" />
      </section>
      <section className="eds-spacing--px-48">
        {manageAccessModal && accessAccountList && (
          <MyAccessForm
            isModalOpen={manageAccessModal}
            accessAccountList={accessAccountList}
            isChinaRegion={isChinaRegion}
            onDismiss={() => setManageAccessModal(false)}
            managerId={managerId}
            swooshRegistrationLink={swooshRegistrationLink}
            sponsorId={sponsorDetails?.sponsorId}
            setSnackBar={setSnackbar}
          />
        )}
        <h2 className={`eds-spacing--my-36 eds-text--align-center txt-align-center h2-25`}>
          {i18nString(ts.MYSTORE_ONLINEBENEFITS_TEXT)}
        </h2>
        {(accessAccountList ?? []).length <= 0 ? (
          <>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton
                data-testid={`pass-skeleton ${index}`}
                key={index}
                height={50}
                width={`${100}%`}
                style={{ margin: 'var(--eds-space-16)' }}
              />
            ))}
          </>
        ) : (
          <div className="eds-flex eds-flex--justify-content-center eds-flex--align-items-flex-center">
            <Table className="eds-table" width="100%">
              <thead>
                <tr key="accessAccountHeader">
                  {columns.map((column, index) => (
                    <TableHeading
                      className={styles.table_header}
                      data-testid={column.headerName}
                      key={index + 1}
                    >
                      {i18nString(ts[column.headerName as keyof typeof ts])}
                    </TableHeading>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(accessAccountList ?? [])
                  .sort((a, b) => a.passTypeId - b.passTypeId)
                  .map(accessAccount => getAccessDetails(accessAccount))}
                {(accessAccountList === undefined || error !== undefined) && (
                  <tr>
                    <TableCell className={styles.table_td} colSpan={4}>
                      {error ? error : 'Loading Access Accounts...'}
                    </TableCell>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </section>
      <Snackbar>
        {snackBar.showSnack && (
          <Snack
            id="myaccess-snackbar"
            className="txt_uppercase"
            autoDismissDuration={5000}
            status={snackBar.snackStatus}
            onDismiss={() => setSnackbar(prev => ({ ...prev, showSnack: false }))}
          >
            {snackBar.message}
          </Snack>
        )}
      </Snackbar>
    </>
  );
};
